import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Plus, Trash2, Calendar } from "lucide-react";
import { useSubtasks } from "@/hooks/useSubtasks";
import { useTaskCreationSettings } from "@/hooks/useTaskCreationSettings";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface SubtasksTabProps {
  parentTaskId: string;
  brandId: string;
  onSubtaskClick?: (subtaskId: string) => void;
  task: {
    source_type?: string;
    sprint_id?: string;
    tag_ids?: string[];
  };
}

export const SubtasksTab = ({ parentTaskId, brandId, onSubtaskClick, task }: SubtasksTabProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { subtasks, isLoading, createSubtask, updateSubtask, deleteSubtask, completedCount, totalCount, progressPercentage } = useSubtasks(parentTaskId);
  const { settings: taskCreationSettings } = useTaskCreationSettings();
  const [newSubtaskTitle, setNewSubtaskTitle] = useState("");
  const [newSubtaskAssignee, setNewSubtaskAssignee] = useState<string>("unassigned");
  const [newSubtaskDueDate, setNewSubtaskDueDate] = useState<Date>();
  const [isAdding, setIsAdding] = useState(false);
  const [confirmingSubtaskId, setConfirmingSubtaskId] = useState<string | null>(null);

  const { data: squadMembers = [] } = useQuery({
    queryKey: ['squad-members', brandId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('growth_squad')
        .select('id, name, alias')
        .eq('brand_id', brandId)
        .order('name');
      
      if (error) throw error;
      return data;
    },
    enabled: !!brandId,
  });

  const handleAddSubtask = async () => {
    if (!newSubtaskTitle.trim() || !user) return;

    // Validate due date is required if setting is enabled
    if (taskCreationSettings?.due_date_required && !newSubtaskDueDate) {
      toast({
        title: "Due date required",
        description: "Please select a due date before creating this subtask",
        variant: "destructive",
      });
      return;
    }

    setIsAdding(true);
    try {
      await createSubtask.mutateAsync({
        parent_id: parentTaskId,
        title: `[Subtask] ${newSubtaskTitle.trim()}`,
        brand_id: brandId,
        user_id: user.id,
        status: 'pending',
        assigned_to: newSubtaskAssignee && newSubtaskAssignee !== 'unassigned' ? newSubtaskAssignee : undefined,
        due_date: newSubtaskDueDate ? format(newSubtaskDueDate, 'yyyy-MM-dd') : undefined,
        source_type: task.source_type,
        sprint_id: task.sprint_id,
        tag_ids: task.tag_ids,
      });
      setNewSubtaskTitle("");
      setNewSubtaskAssignee("unassigned");
      setNewSubtaskDueDate(undefined);
    } finally {
      setIsAdding(false);
    }
  };

  const handleToggleStatus = (subtaskId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'done' ? 'pending' : 'done';
    updateSubtask.mutate({
      id: subtaskId,
      updates: { status: newStatus },
    });
  };

  const handleConfirmDelete = (subtaskId: string) => {
    setConfirmingSubtaskId(null);
    deleteSubtask.mutate(subtaskId);
  };

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading subtasks...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Progress indicator */}
      {totalCount > 0 && (
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium">{completedCount}/{totalCount} completed</span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </div>
      )}

      {/* Subtasks list */}
      <div className="space-y-1.5">
        {subtasks.map((subtask) => {
          const assignedMember = squadMembers.find(m => m.id === subtask.assigned_to);
          const isConfirming = confirmingSubtaskId === subtask.id;

          return (
            <div key={subtask.id} className="p-2 border rounded hover:bg-accent/50 transition-colors">
              <div className="flex items-center gap-2 group">
                <Checkbox
                  checked={subtask.status === 'done'}
                  onCheckedChange={() => handleToggleStatus(subtask.id, subtask.status)}
                />
                <div
                  className="flex-1 flex items-center gap-3 cursor-pointer min-w-0"
                  onClick={() => onSubtaskClick?.(subtask.id)}
                >
                  <span
                    className={cn(
                      "text-sm font-medium truncate flex-1",
                      subtask.status === 'done' && "line-through text-muted-foreground"
                    )}
                  >
                    {subtask.title}
                  </span>
                  {assignedMember && (
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {assignedMember.alias || assignedMember.name}
                    </span>
                  )}
                  {subtask.due_date && (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                      <Calendar className="h-3 w-3" />
                      {format(new Date(subtask.due_date), 'MMM d')}
                    </span>
                  )}
                </div>
                {!isConfirming && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmingSubtaskId(subtask.id);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                )}
              </div>

              {isConfirming && (
                <div className="mt-2 flex justify-end items-center gap-2 text-xs text-muted-foreground">
                  <span>Delete this subtask?</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmingSubtaskId(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleConfirmDelete(subtask.id)}
                  >
                    Delete
                  </Button>
                </div>
              )}
            </div>
          );
        })}

        {subtasks.length === 0 && (
          <div className="text-center py-8 text-sm text-muted-foreground">
            No subtasks yet. Add one below to break down this task.
          </div>
        )}
      </div>

      {/* Add new subtask */}
      <div className="flex gap-2 items-center">
        <Input
          placeholder="Subtask title..."
          value={newSubtaskTitle}
          onChange={(e) => setNewSubtaskTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleAddSubtask();
            }
          }}
          disabled={isAdding}
          className="flex-1"
        />
        
        <div className="flex items-center gap-2">
          {newSubtaskAssignee && newSubtaskAssignee !== 'unassigned' ? (
            <div className="flex items-center gap-1">
              <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center flex-shrink-0">
                {squadMembers.find(m => m.id === newSubtaskAssignee)?.alias?.slice(0, 2).toUpperCase() || 'U'}
              </div>
              <span className="text-sm whitespace-nowrap">
                {squadMembers.find(m => m.id === newSubtaskAssignee)?.alias ||
                 squadMembers.find(m => m.id === newSubtaskAssignee)?.name}
              </span>
            </div>
          ) : (
            <span className="text-sm text-muted-foreground whitespace-nowrap">Unassigned</span>
          )}
          
          <Select value={newSubtaskAssignee || 'unassigned'} onValueChange={setNewSubtaskAssignee}>
            <SelectTrigger className="h-8 text-sm w-[120px]">
              <SelectValue>
                Change
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="min-w-[220px]">
              <SelectItem value="unassigned">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-gray-200 text-gray-600 text-xs flex items-center justify-center">
                    ?
                  </div>
                  <span>Unassigned</span>
                </div>
              </SelectItem>
              {squadMembers.map((member) => (
                <SelectItem key={member.id} value={member.id}>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
                      {member.alias?.slice(0, 2).toUpperCase() || 'U'}
                    </div>
                    <span className="truncate">{member.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-8 w-[100px] text-sm">
              <Calendar className="h-4 w-4 mr-1" />
              {newSubtaskDueDate ? format(newSubtaskDueDate, 'MMM d') : 'Due...'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="single"
              selected={newSubtaskDueDate}
              onSelect={setNewSubtaskDueDate}
              disabled={(date) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                // Check same-day restriction
                if (taskCreationSettings?.allow_same_day_tasks === false) {
                  const dateToCheck = new Date(date);
                  dateToCheck.setHours(0, 0, 0, 0);
                  if (dateToCheck.getTime() === today.getTime()) {
                    return true; // Disable today
                  }
                }
                
                // Check sprint date restriction - use parent task's sprint if available
                if (taskCreationSettings?.allow_off_sprint_dates === false && task.sprint_id) {
                  // Would need to fetch sprint data to validate, for now just prevent past dates
                  return date < today;
                }
                
                // Default: only prevent past dates
                return date < today;
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>

        <Button
          onClick={handleAddSubtask}
          disabled={!newSubtaskTitle.trim() || isAdding}
          size="default"
          className="h-8 w-8 p-0"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
